import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fetchSupplierReturn } from '@/api';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { SupplierReturn } from '@/types/supplier';

interface ViewReturnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  returnId: string | null;
}

export function ViewReturnDialog({ open, onOpenChange, returnId }: ViewReturnDialogProps) {
  const currencySymbol = useCurrencySymbol();
  const [record, setRecord] = useState<SupplierReturn | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !returnId) {
      setRecord(null);
      return;
    }
    setLoading(true);
    fetchSupplierReturn(returnId)
      .then(res => setRecord({ ...res.data, total_amount: Number(res.data.total_amount) || 0 }))
      .catch(() => setRecord(null))
      .finally(() => setLoading(false));
  }, [open, returnId]);

  const formatDate = (date: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date || '');
    if (match) {
      const [, year, month, day] = match;
      return `${month}/${day}/${year}`;
    }
    return date;
  };

  const handleDownload = () => {
    if (!record) return;
    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.text(`Supplier Return ${record.return_number}`, 14, 18);
    doc.setFontSize(10);
    doc.text(`Date: ${formatDate(record.date)}`, 14, 26);
    doc.text(`Supplier: ${record.supplier_name || record.supplier_id}`, 14, 32);
    if (record.invoice_number) {
      doc.text(`Against Invoice: ${record.invoice_number}`, 14, 38);
    }
    if (record.reason) {
      doc.text(`Reason: ${record.reason}`, 14, 44);
    }

    const rows = (record.items || []).map(item => [
      item.item_name,
      item.item_sku || '—',
      String(item.quantity),
      `${currencySymbol}${Number(item.unit_price).toFixed(2)}`,
      `${currencySymbol}${Number(item.total_price).toFixed(2)}`,
    ]);

    autoTable(doc, {
      startY: 52,
      head: [['Item', 'SKU', 'Qty', 'Unit Price', 'Total']],
      body: rows,
      styles: { fontSize: 9 },
      foot: [
        [
          { content: 'Total Credited', colSpan: 4, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: `${currencySymbol}${record.total_amount.toFixed(2)}`, styles: { fontStyle: 'bold' } }
        ]
      ],
    });

    doc.save(`Supplier-Return-${record.return_number}.pdf`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <DialogTitle className="text-xl font-bold">
            Return Details {record ? `- ${record.return_number}` : ''}
          </DialogTitle>
          {record && (
            <Button variant="outline" size="sm" onClick={handleDownload}>
              Download PDF
            </Button>
          )}
        </DialogHeader>

        {loading ? (
          <div className="text-center py-10 text-muted-foreground">Loading return...</div>
        ) : !record ? (
          <div className="text-center py-10 text-muted-foreground">Return not found</div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Return Number:</span>
                  <span className="font-semibold">{record.return_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span>{formatDate(record.date)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Supplier:</span>
                  <span>{record.supplier_name || record.supplier_id}</span>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Against Invoice:</span>
                  <span>
                    {record.invoice_number
                      ? <Badge variant="outline">{record.invoice_number}</Badge>
                      : <Badge variant="secondary">Bulk return</Badge>}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Reason:</span>
                  <span>{record.reason || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Credited:</span>
                  <span className="font-bold text-lg text-green-600">
                    {currencySymbol}{record.total_amount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <span>Items Returned</span>
                <Badge variant="outline">
                  {(record.items || []).length} item{(record.items || []).length !== 1 ? 's' : ''}
                </Badge>
              </h3>
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="font-semibold">Item Name</TableHead>
                        <TableHead className="font-semibold">SKU</TableHead>
                        <TableHead className="font-semibold text-center">Quantity</TableHead>
                        <TableHead className="font-semibold text-right">Unit Price</TableHead>
                        <TableHead className="font-semibold text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(record.items || []).map((item, index) => (
                        <TableRow key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <TableCell className="font-medium">{item.item_name}</TableCell>
                          <TableCell className="text-gray-600">{item.item_sku || '—'}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">{item.quantity}</Badge>
                          </TableCell>
                          <TableCell className="text-right">{currencySymbol}{Number(item.unit_price).toFixed(2)}</TableCell>
                          <TableCell className="text-right font-bold">{currencySymbol}{Number(item.total_price).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
